import type { CacheTable } from "@/hooks/use-cached-query";

export interface SharedCacheEntry {
    table: CacheTable;
    cacheKey: string;
    ttl: number;
}

const MINUTE = 60 * 1000;

/**
 * Single source of truth for shared Convex query caching.
 *
 * Every query whose result is shared between the DataPreloader and page
 * components MUST use one of these entries so the cache key, table, and TTL
 * never drift apart (previously `aurora-reports-all` was cached with 5 min in
 * one file and 10 min in another).
 *
 * TTL only controls when the client revalidates — stale rows are still served
 * instantly for local-first UX (see dexie-db.ts).
 */
export const SHARED_CACHE = {
    /** Latest single satellite reading — dashboard headline, map. */
    satelliteLatest: {
        table: "satelliteData",
        cacheKey: "satellite-latest",
        ttl: 2 * MINUTE,
    },
    /** Most recent ~300 readings (~5h at 1/min) — dashboard, forecasts, map. */
    satelliteRecent300: {
        table: "satelliteData",
        cacheKey: "satellite-recent-300",
        ttl: 3 * MINUTE,
    },
    satelliteRange24h: {
        table: "satelliteData",
        cacheKey: "satellite-range-24h",
        ttl: 5 * MINUTE,
    },
    satelliteRange48h: {
        table: "satelliteData",
        cacheKey: "satellite-range-48h",
        ttl: 5 * MINUTE,
    },
    satelliteRange7d: {
        table: "satelliteData",
        cacheKey: "satellite-range-7d",
        ttl: 10 * MINUTE,
    },
    wsaForecastRange24h: {
        table: "wsaData",
        cacheKey: "wsa-forecast-range-24h",
        ttl: 60 * MINUTE,
    },
    wsaForecastRange48h: {
        table: "wsaData",
        cacheKey: "wsa-forecast-range-48h",
        ttl: 60 * MINUTE,
    },
    wsaForecastRange7d: {
        table: "wsaData",
        cacheKey: "wsa-forecast-range-7d",
        ttl: 60 * MINUTE,
    },
    /** WSA-Enlil chart samples (large payload, model updates ~daily). */
    wsaEarthData4000: {
        table: "wsaData",
        cacheKey: "wsa-earth-data-4000",
        ttl: 60 * MINUTE,
    },
    /** Smaller WSA sample used by the text forecast. */
    wsaEarthData600: {
        table: "wsaData",
        cacheKey: "wsa-earth-data-600",
        ttl: 60 * MINUTE,
    },
    /** Hourly-aggregated WSA forecast for the next 72h — hour strip + long-term page. */
    wsaHourly72h: {
        table: "wsaData",
        cacheKey: "wsa-hourly-72h",
        ttl: 60 * MINUTE,
    },
    wsaLatestModelRun: {
        table: "wsaData",
        cacheKey: "wsa-latest-model-run",
        ttl: 5 * MINUTE,
    },
    /** Community aurora reports with user names. */
    auroraReportsAll: {
        table: "auroraReports",
        cacheKey: "aurora-reports-all",
        ttl: 10 * MINUTE,
    },
    /** Cloud reports for the current night window (map overlay). */
    cloudReportsMap: {
        table: "cloudReports",
        cacheKey: "cloud-reports-map",
        ttl: 5 * MINUTE,
    },
    publicLocations: {
        table: "publicLocations",
        cacheKey: "public-locations-map",
        ttl: 30 * MINUTE,
    },
    spaceWeatherRegions: {
        table: "spaceWeather",
        cacheKey: "space-weather-regions-v3",
        ttl: 5 * MINUTE,
    },
    spaceWeatherCmes: {
        table: "spaceWeather",
        cacheKey: "space-weather-cmes",
        ttl: 5 * MINUTE,
    },
    spaceWeatherMagnetometer: {
        table: "spaceWeather",
        cacheKey: "space-weather-magnetometer",
        ttl: 2 * MINUTE,
    },
    spaceWeatherHpi: {
        table: "spaceWeather",
        cacheKey: "space-weather-hpi",
        ttl: 2 * MINUTE,
    },
    spaceWeatherStationReadings: {
        table: "spaceWeather",
        cacheKey: "space-weather-station-readings",
        ttl: 3 * MINUTE,
    },
    /** Active ML model artifacts — needed offline to compute the D-index locally. */
    dIndexActiveModels: {
        table: "dIndexModelArtifacts",
        cacheKey: "d-index-active-models",
        ttl: 15 * MINUTE,
    },
} as const satisfies Record<string, SharedCacheEntry>;

export type SharedCacheKey = keyof typeof SHARED_CACHE;

/** Entries hydrated from IndexedDB into memory at startup (see DataPreloader). */
export const WARMUP_ENTRIES: SharedCacheEntry[] = Object.values(SHARED_CACHE);
